import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, Plus, X } from 'lucide-react';
import {
  LEAVE_STATUSES,
  LEAVE_STATUS_LABELS,
  LEAVE_TYPES,
  LEAVE_TYPE_LABELS,
  type Employee,
  type Leave,
  type LeaveType,
  type Paginated,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDate } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { todayISO } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

const STATUS_TONE: Record<string, BadgeKind> = {
  PENDENTE: 'warn',
  APROVADO: 'ok',
  REPROVADO: 'bad',
  CANCELADO: 'mute',
};

/**
 * Férias e afastamentos.
 *
 * Aprovar aqui muda a situação do colaborador (Em férias / Afastado) — é o que
 * faz o encarregado saber quem não sobe na obra amanhã sem precisar perguntar
 * ao RH.
 */
export function AfastamentosPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [acting, setActing] = useState<{ leave: Leave; kind: 'approve' | 'reject' | 'cancel' } | null>(
    null,
  );

  const listQuery = useQuery({
    queryKey: ['leaves', { search, status, type, page }],
    queryFn: () =>
      api.get<Paginated<Leave>>('/leaves', {
        search: search || undefined,
        status: status || undefined,
        type: type || undefined,
        page,
        perPage: 25,
      }),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['leaves'] });
    void queryClient.invalidateQueries({ queryKey: ['employees'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const actionMutation = useMutation({
    mutationFn: (payload: { id: string; path: string; body?: unknown }) =>
      api.post(`/leaves/${payload.id}/${payload.path}`, payload.body ?? {}),
    onSuccess: () => {
      setActing(null);
      refresh();
    },
  });

  const columns: Array<Column<Leave>> = [
    { key: 'employeeName', label: 'Colaborador', strong: true, render: (row) => blank(row.employeeName) },
    { key: 'registration', label: 'Matrícula', mono: true, render: (row) => blank(row.registration) },
    { key: 'type', label: 'Tipo', render: (row) => LEAVE_TYPE_LABELS[row.type] },
    { key: 'startDate', label: 'Início', mono: true, render: (row) => formatDate(row.startDate) },
    { key: 'endDate', label: 'Fim', mono: true, render: (row) => formatDate(row.endDate) },
    {
      key: 'totalDays',
      label: 'Dias',
      right: true,
      mono: true,
      render: (row) => row.totalDays,
    },
    {
      key: 'status',
      label: 'Situação',
      render: (row) => (
        <StatusBadge kind={STATUS_TONE[row.status] ?? 'mute'}>
          {LEAVE_STATUS_LABELS[row.status]}
        </StatusBadge>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 150,
      right: true,
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {row.status === 'PENDENTE' && can('afastamentos:aprovar') && (
            <>
              <Button
                small
                variant="ghost"
                icon={Check}
                aria-label={`Aprovar afastamento de ${row.employeeName ?? ''}`}
                onClick={() => setActing({ leave: row, kind: 'approve' })}
              />
              <Button
                small
                variant="ghost"
                icon={X}
                aria-label={`Reprovar afastamento de ${row.employeeName ?? ''}`}
                onClick={() => setActing({ leave: row, kind: 'reject' })}
              />
            </>
          )}
          {['PENDENTE', 'APROVADO'].includes(row.status) && can('afastamentos:excluir') && (
            <Button
              small
              variant="ghost"
              icon={Ban}
              aria-label={`Cancelar afastamento de ${row.employeeName ?? ''}`}
              onClick={() => setActing({ leave: row, kind: 'cancel' })}
            />
          )}
        </span>
      ),
    },
  ];

  const hasFilters = Boolean(search || status || type);

  return (
    <>
      <WorkspaceHeader
        title="Férias e afastamentos"
        actions={
          can('afastamentos:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Solicitar afastamento
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por colaborador ou matrícula…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'status',
              label: 'Situação',
              value: status,
              options: LEAVE_STATUSES.map((value) => ({ value, label: LEAVE_STATUS_LABELS[value] })),
              onChange: (value) => {
                setStatus(value);
                setPage(1);
              },
            },
            {
              key: 'type',
              label: 'Tipo',
              value: type,
              options: LEAVE_TYPES.map((value) => ({ value, label: LEAVE_TYPE_LABELS[value] })),
              onChange: (value) => {
                setType(value);
                setPage(1);
              },
            },
          ]}
        />

        {listQuery.isPending && <TableSkeleton columns={8} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Férias e afastamentos"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setStatus('');
                    setType('');
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhum afastamento registrado"
                  description="Férias, atestado e licença passam por aqui e mudam a situação do colaborador."
                  action={
                    can('afastamentos:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                        Solicitar afastamento
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}
      </div>

      <LeaveModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          refresh();
        }}
      />

      <ConfirmDialog
        open={acting?.kind === 'approve'}
        loading={actionMutation.isPending}
        title="Aprovar este afastamento?"
        confirmLabel="Aprovar"
        message={
          <>
            {acting?.leave.employeeName} passa a constar como{' '}
            <strong>
              {acting?.leave.type === 'FERIAS' ? 'Em férias' : 'Afastado'}
            </strong>{' '}
            no período de {acting ? formatDate(acting.leave.startDate) : ''} a{' '}
            {acting ? formatDate(acting.leave.endDate) : ''}.
            {actionMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(actionMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setActing(null);
          actionMutation.reset();
        }}
        onConfirm={() => acting && actionMutation.mutate({ id: acting.leave.id, path: 'approve' })}
      />

      <ConfirmDialog
        open={acting?.kind === 'reject'}
        destructive
        requireReason
        loading={actionMutation.isPending}
        title="Reprovar este afastamento?"
        confirmLabel="Reprovar"
        message="Explique o motivo — quem solicitou vai ler."
        onClose={() => {
          setActing(null);
          actionMutation.reset();
        }}
        onConfirm={(reason) =>
          acting && actionMutation.mutate({ id: acting.leave.id, path: 'reject', body: { reason } })
        }
      />

      <ConfirmDialog
        open={acting?.kind === 'cancel'}
        destructive
        loading={actionMutation.isPending}
        title="Cancelar este afastamento?"
        confirmLabel="Cancelar afastamento"
        message="O colaborador volta para Ativo e o registro fica no histórico."
        onClose={() => {
          setActing(null);
          actionMutation.reset();
        }}
        onConfirm={() => acting && actionMutation.mutate({ id: acting.leave.id, path: 'cancel' })}
      />
    </>
  );
}

function LeaveModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [type, setType] = useState<LeaveType>('FERIAS');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [reason, setReason] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setEmployeeId('');
      setType('FERIAS');
      setStartDate(todayISO());
      setEndDate(todayISO());
      setReason('');
    }
  }

  // Quem está de férias hoje ainda AGENDA o próximo afastamento — só o
  // desligado fica de fora, que é a mesma regra do servidor.
  const employeesQuery = useQuery({
    queryKey: ['employees', 'leave-eligible'],
    queryFn: () => api.get<Paginated<Employee>>('/employees', { perPage: 200 }),
    enabled: open,
    select: (response) => ({
      ...response,
      data: response.data.filter((employee) => employee.status !== 'DESLIGADO'),
    }),
  });

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post('/leaves', body),
    onSuccess: onCreated,
  });

  const errors = fieldErrors(mutation.error);
  const invalidRange = endDate < startDate;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Solicitar afastamento"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!employeeId || invalidRange}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                employeeId,
                type,
                startDate,
                endDate,
                reason: reason.trim() || null,
              })
            }
          >
            Solicitar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Colaborador" htmlFor="leave-employee" error={errors.employeeId}>
          <select
            id="leave-employee"
            className="input"
            value={employeeId}
            onChange={(event) => setEmployeeId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(employeesQuery.data?.data ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.registration} · {employee.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Tipo" htmlFor="leave-type">
          <select
            id="leave-type"
            className="input"
            value={type}
            onChange={(event) => setType(event.target.value as LeaveType)}
          >
            {LEAVE_TYPES.map((value) => (
              <option key={value} value={value}>
                {LEAVE_TYPE_LABELS[value]}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid-2">
          <Field label="Início" htmlFor="leave-start" error={errors.startDate}>
            <DateInput
              id="leave-start"
              className="input input--mono"
              value={startDate}
              onChange={(iso) => setStartDate(iso)}
            />
          </Field>
          <Field
            label="Fim"
            htmlFor="leave-end"
            error={errors.endDate ?? (invalidRange ? 'O fim não pode ser antes do início' : undefined)}
          >
            <DateInput
              id="leave-end"
              className="input input--mono"
              value={endDate}
              onChange={(iso) => setEndDate(iso)}
            />
          </Field>
        </div>

        <Field label="Observação" htmlFor="leave-reason">
          <textarea
            id="leave-reason"
            className="input"
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </FormSection>
    </Modal>
  );
}
